import { FC, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Typography } from '@kizen/kds/Typography';
import { Button, type HoverColor } from '@kizen/kds/Button';
import { useTheme } from 'hooks/useTheme';

export type QuickResponseOption =
  | string
  | { label: string; value: string; url?: string };

export type QuickResponseData = {
  options: QuickResponseOption[];
  selectType: 'single_select' | 'multi_select' | 'action';
};

type AutoResponsesProps = {
  isMessageResponseLoading: boolean;
  data: QuickResponseData;
  onSelect: (
    id: string,
    selectType: QuickResponseData['selectType'],
    url?: string
  ) => void;
  selectedResponses: { id: string; value: string }[];
  header?: string;
  handleSubmit: () => void;
  onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  showSendButton?: boolean;
  maxHeight?: number;
  noTruncateOption?: string;
};

type AutoResponseButtonProps = {
  key: string;
  onClick: (answer: string, url?: string) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLButtonElement>) => void;
  selected: boolean;
  option: QuickResponseOption;
  fullWidth?: boolean;
  noTruncate?: boolean;
};

const AutoResponseButton: FC<AutoResponseButtonProps> = ({
  onClick,
  onKeyDown,
  option,
  selected,
  fullWidth,
  noTruncate,
}) => {
  const displayText = typeof option === 'string' ? option : option.label;
  const value = typeof option === 'string' ? option : option.value;
  const url = typeof option === 'string' ? undefined : option.url;

  const baseClasses =
    'p-3 z-10 rounded-[20px] border border-border-default focus:outline-none overflow-hidden';
  const widthClass = fullWidth ? 'w-full min-w-0' : '';
  const colorClasses = selected
    ? 'bg-ai-user-bg text-white'
    : 'bg-background-white';

  // prevent truncation for add more details option
  const noTruncatedClasses = noTruncate
    ? 'text-nowrap min-w-[135px]'
    : 'text-wrap ';

  const shouldTruncate = fullWidth && !noTruncate;

  return (
    <button
      className={`${baseClasses} ${widthClass} ${colorClasses} ${noTruncatedClasses}`}
      onClick={(event) => {
        event.preventDefault();
        onClick(value, url);
      }}
      onKeyDown={onKeyDown}
    >
      <div className="w-full min-w-0">
        <Typography truncate={shouldTruncate} modalCompatibility enableTooltip>
          {displayText}
        </Typography>
      </div>
    </button>
  );
};

const hashOptions = (options: QuickResponseOption[]): string =>
  options.map((o) => (typeof o === 'string' ? o : o.value)).join('\x00');

export const QuickResponses: FC<AutoResponsesProps> = ({
  isMessageResponseLoading,
  data,
  onSelect,
  selectedResponses,
  header,
  handleSubmit,
  onKeyDown,
  showSendButton = true,
  maxHeight,
  noTruncateOption,
}) => {
  // Incrementing key forces a fresh DOM element so animate-fadeUp always restarts
  const [animKey, setAnimKey] = useState(0);
  const hashRef = useRef(hashOptions(data.options));

  useEffect(() => {
    const newHash = hashOptions(data.options);
    if (newHash === hashRef.current) return;
    const wasEmpty = hashRef.current === '';
    hashRef.current = newHash;
    if (wasEmpty) return;
    setAnimKey((k) => k + 1);
  }, [data]);

  const { getSemanticColor } = useTheme();

  if (data.options.length === 0) return null;

  // Use grid layout when maxHeight is provided and there are more options than maxHeight (not for actions)
  const showGridLayout = Boolean(
    maxHeight && data.options.length > maxHeight && data.selectType !== 'action'
  );

  const containerClassName = showGridLayout
    ? 'grid gap-[10px] w-full'
    : 'flex flex-wrap gap-[10px] h-full w-full';

  const containerStyle = showGridLayout
    ? {
        gridTemplateColumns: 'repeat(2, 1fr)',
      }
    : undefined;

  return (
    <div
      key={animKey}
      className={`w-full flex justify-between items-end ${animKey > 0 ? 'animate-fadeUp' : ''} mb-[10px] px-[5px] pr-[15px] pt-[5px]`}
    >
      <div className="flex flex-col gap-[10px] min-w-0 flex-1">
        {(!isMessageResponseLoading && data.options.length > 0) ||
        (data.selectType === 'action' && data.options.length > 0) ? (
          <div className="pl-[5px]">
            <Typography size="sm" weight="bold" color="font-secondary">
              {header}
            </Typography>
          </div>
        ) : null}
        <div
          className={`${containerClassName} max-h-[50vh] overflow-y-auto kds-expanding-scrollbar`}
          style={containerStyle}
        >
          {(!isMessageResponseLoading && data.options.length > 0) ||
          data.selectType === 'action'
            ? data.options.map((option, idx) => {
                const value =
                  typeof option === 'string' ? option : option.value;
                const isNoTruncateOption =
                  noTruncateOption !== undefined && value === noTruncateOption;
                return (
                  <AutoResponseButton
                    key={`auto-response-${idx}-${showGridLayout}`}
                    onClick={(selectedValue, url) => {
                      onSelect(selectedValue, data.selectType, url);
                    }}
                    onKeyDown={onKeyDown as any}
                    option={option}
                    selected={Boolean(
                      selectedResponses?.find((r) => r.value === value)
                    )}
                    fullWidth={showGridLayout}
                    noTruncate={showGridLayout && isNoTruncateOption}
                  />
                );
              })
            : null}
        </div>
      </div>
      {selectedResponses.length > 0 && showSendButton ? (
        <div className="animate-fadeUp text-button-tertiary-default">
          <Button
            rightIcon="action-send-message"
            variant="text"
            color="inherit"
            hoverColor={getSemanticColor('ai-user-bg') as HoverColor}
            onClick={() => handleSubmit()}
          />
        </div>
      ) : null}
    </div>
  );
};
